<script setup lang="ts">
import type { Domain } from '@gro-up/types'
import { Container, Grid, Typography } from '@gro-up/ui'
import { useResponsive } from '@gro-up/ui/composables'
import type { LngLatBounds } from 'mapbox-gl'
import { AccordionContent, AccordionHeader, AccordionItem, AccordionRoot, AccordionTrigger } from 'radix-vue'
import { computed, onMounted, ref } from 'vue'

import ContactAddress, { type ContactAddressProps } from './ContactAddress.vue'
import ContactContent, { type ContentProps } from './ContactContent.vue'
import ContactOpening, { type OpeningHoursProps } from './ContactOpening.vue'

export type ContactComponentProps = {
  title: string
  sections: (OpeningHoursProps | ContentProps | ContactAddressProps)[]
  mapView: {
    accessToken: string
    initialCenter: [number, number]
    initialZoom: number
    bounds?: LngLatBounds | null
  }
  domain?: Domain
}

const { title, sections, mapView, domain } = defineProps<ContactComponentProps>()

const { isMaxLg } = useResponsive()

const mounted = ref(false)
onMounted(() => {
  setTimeout(() => {
    mounted.value = true
  }, 300)
})

// Create location marker for the map
const locationMarker = computed(() => {
  const [longitude, latitude] = mapView.initialCenter

  const marker = {
    latitude,
    longitude,
    title: title,
  }

  return marker
})
</script>

<template>
  <Container as="section" data-component="ContactComponent" :full-width-on-mobile="true">
    <Grid class="h-full bg-white rounded-2xl overflow-hidden">
      <div class="col-start-1 col-end-13" :class="$slots.map ? 'lg:col-end-9' : 'lg:col-end-13'">
        <div class="flex flex-col lg:gap-8 px-4 py-6 lg:px-10 lg:pt-10 lg:pb-14">
          <Typography as="h3" variant="header-3">{{ title }}</Typography>

          <!-- Mobile: Accordion layout -->
          <AccordionRoot v-if="isMaxLg" type="single" collapsible>
            <AccordionItem
              v-for="(section, index) in sections"
              :key="`contact-section-${index}`"
              :value="section.title"
              class="overflow-hidden">
              <AccordionHeader>
                <AccordionTrigger class="w-full flex items-center justify-between gap-3 py-5 group cursor-pointer">
                  <Typography as="header" variant="header-5" class="text-left">{{ section.title }}</Typography>
                  <div class="shrink-0 w-3 h-3 relative group-hover:scale-[0.667] transition-transform duration-200">
                    <div class="w-full h-0.5 bg-current absolute top-1/2 -translate-y-1/2" />
                    <div
                      class="w-full h-0.5 bg-current absolute top-1/2 -translate-y-1/2 rotate-90 group-data-[state=open]:rotate-0 transition-transform duration-200" />
                  </div>
                </AccordionTrigger>
              </AccordionHeader>
              <AccordionContent
                class="overflow-hidden data-[state=open]:animate-[slide-down_ease-out] data-[state=closed]:animate-[slide-up_ease-out_forwards]"
                :style="{ animationDuration: mounted ? '300ms' : '0ms' }"
                force-mount>
                <div class="pb-5">
                  <component
                    :is="
                      section.type === 'openingHours'
                        ? ContactOpening
                        : section.type === 'contact'
                          ? ContactAddress
                          : ContactContent
                    "
                    v-bind="section"
                    :hide-title="true">
                    <template v-if="section.type === 'contact' && $slots.map" #map>
                      <slot
                        name="map"
                        :location-marker="locationMarker"
                        :map-view="mapView"
                        :domain="domain" />
                    </template>
                  </component>
                </div>
              </AccordionContent>
            </AccordionItem>
          </AccordionRoot>

          <!-- Desktop: Static flex layout -->
          <div v-else class="flex flex-wrap justify-between gap-10">
            <div
              v-for="(section, index) in sections"
              :key="`contact-section-${index}`"
              class="w-full lg:w-[calc(50%-20px)] max-w-[283px]">
              <component
                :is="
                  section.type === 'openingHours'
                    ? ContactOpening
                    : section.type === 'contact'
                      ? ContactAddress
                      : ContactContent
                "
                v-bind="section" />
            </div>
          </div>
        </div>
      </div>
      <div v-if="$slots.map" class="hidden lg:block lg:col-start-9 lg:col-end-13 overflow-hidden">
        <slot
          name="map"
          :location-marker="locationMarker"
          :map-view="mapView"
          :domain="domain" />
      </div>
    </Grid>
  </Container>
</template>

<style>
@keyframes slide-down {
  from {
    height: 0;
  }
  to {
    height: var(--radix-accordion-content-height);
  }
}

@keyframes slide-up {
  from {
    height: var(--radix-accordion-content-height);
  }
  to {
    height: 0;
  }
}
</style>
